import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Alert,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Print as PrintIcon,
  GetApp as DownloadIcon,
  Refresh as RefreshIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import accountingService from '../../services/accountingService';
import realDataAccountingService from '../../services/realDataAccountingService';
import {
  setBalanceSheet,
  setLoading,
  setError as setReduxError,
  setPrintData,
  setChartOfAccounts
} from '../../redux/accounting';

const BalanceSheet = () => {
  const dispatch = useDispatch();
  const { balanceSheet, loading: reduxLoading, chartOfAccounts } = useSelector(state => state.accounting);
  const { admin } = useSelector(state => state.admin);
  const schooldetails = useSelector(state => state.schooldetails || []);
  
  // Validate that all actions are properly imported at component level
  useEffect(() => {
    if (!setLoading || !setBalanceSheet || !setReduxError || !setChartOfAccounts || !setPrintData) {
      console.error('❌ Redux actions validation failed:', {
        setLoading: typeof setLoading,
        setBalanceSheet: typeof setBalanceSheet,
        setReduxError: typeof setReduxError,
        setChartOfAccounts: typeof setChartOfAccounts,
        setPrintData: typeof setPrintData
      });
    }
  }, []);
  
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(0, 1); // Start of year
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLocalLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isLoadingBalanceSheet, setIsLoadingBalanceSheet] = useState(false);

  // Get current school with fallback for different property names
  const currentSchool = Array.isArray(schooldetails) && schooldetails.length > 0 
    ? schooldetails[0] 
    : (schooldetails && typeof schooldetails === 'object' && !Array.isArray(schooldetails) 
      ? schooldetails 
      : null);
  
  // Get schoolID with multiple fallback options
  const schoolID = currentSchool?.schoolID || currentSchool?._id || currentSchool?.id || null;

  // Get Redux data at component level - using correct structure
  // Handle both data structures: state.students?.data || state.students
  // All useSelector hooks must be at the top level, not inside object literals
  const studentsData = useSelector(state => state.students?.data || state.students || []);
  const teachersData = useSelector(state => state.teachers?.data || state.teachers || []);
  const expensesData = useSelector(state => state.expenses?.data || state.expenses || []);
  const salesData = useSelector(state => state.sales?.sales || state.sales || []);
  const assetsData = useSelector(state => state.assets?.assets || state.assets || []);
  const classData = useSelector(state => state.class?.data || state.class || []);
  // Products / Inventory (from sales slice)
  const productsData = useSelector(state => state.sales?.products || []);
  
  // Create reduxData object from the selector results - memoized to prevent unnecessary re-renders
  const reduxData = useMemo(() => ({
    students: Array.isArray(studentsData) ? studentsData : [],
    teachers: Array.isArray(teachersData) ? teachersData : [],
    expenses: Array.isArray(expensesData) ? expensesData : [],
    sales: Array.isArray(salesData) ? salesData : [],
    assets: Array.isArray(assetsData) ? assetsData : [],
    class: Array.isArray(classData) ? classData : [],
    // Include products so inventory can use real stock data
    products: Array.isArray(productsData) ? productsData : []
  }), [studentsData, teachersData, expensesData, salesData, assetsData, classData, productsData]);

  const loadBalanceSheet = useCallback(async () => {
    // Prevent multiple simultaneous loads
    if (isLoadingBalanceSheet) {
      console.log('Balance sheet is already loading, skipping...');
      return;
    }
    
    // Validate that all required actions are available first
    if (!setLoading || !setBalanceSheet || !setReduxError || !setChartOfAccounts) {
      console.error('Redux actions not properly imported:', {
        setLoading: !!setLoading,
        setBalanceSheet: !!setBalanceSheet,
        setReduxError: !!setReduxError,
        setChartOfAccounts: !!setChartOfAccounts
      });
      setError('Redux actions not properly initialized. Please refresh the page.');
      setLocalLoading(false);
      setIsLoadingBalanceSheet(false);
      return;
    }
    
    if (!schoolID) {
      setError('No school selected. Please select a school first.');
      setLocalLoading(false);
      setIsLoadingBalanceSheet(false);
      return;
    }
    
    // Validate date range
    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date must be before end date');
      setLocalLoading(false);
      setIsLoadingBalanceSheet(false);
      return;
    }
    
    setIsLoadingBalanceSheet(true);
    setLocalLoading(true);
    setError(null);
    
    // Safe dispatch wrapper to ensure we never dispatch undefined
    const safeDispatch = (actionCreator, payload) => {
      try {
        if (!actionCreator) {
          console.error('Action creator is undefined');
          return;
        }
        if (typeof actionCreator !== 'function') {
          console.error('Action creator is not a function:', typeof actionCreator, actionCreator);
          return;
        }
        const action = actionCreator(payload);
        if (!action) {
          console.error('Action creator returned undefined or null:', actionCreator.name || 'unknown');
          return;
        }
        if (typeof action !== 'object') {
          console.error('Invalid action object type:', typeof action, action);
          return;
        }
        if (!action.type) {
          console.error('Action missing type property:', action);
          return;
        }
        dispatch(action);
      } catch (err) {
        console.error('Error in safeDispatch:', err, 'Action creator:', actionCreator?.name || 'unknown');
      }
    };
    
    // Dispatch loading state directly with validation
    try {
      if (!setLoading) {
        console.error('setLoading is undefined');
      } else if (typeof setLoading !== 'function') {
        console.error('setLoading is not a function:', typeof setLoading);
      } else {
        const loadingAction = setLoading({ balanceSheet: true });
        // Redux Toolkit actions are always objects, dispatch if valid
        if (loadingAction && typeof loadingAction === 'object') {
          dispatch(loadingAction);
        } else if (loadingAction === undefined) {
          console.error('setLoading returned undefined - this may indicate a build/import issue');
        } else {
          console.error('setLoading returned unexpected value:', typeof loadingAction, loadingAction);
        }
      }
    } catch (err) {
      console.error('Error setting Redux loading state:', err);
    }
    
    try {
      console.log('Loading balance sheet with date range:', startDate, 'to', endDate);
      console.log('School ID:', schoolID);
      console.log('Redux data summary:', {
        students: reduxData.students?.length || 0,
        teachers: reduxData.teachers?.length || 0,
        expenses: reduxData.expenses?.length || 0,
        sales: reduxData.sales?.length || 0,
        assets: reduxData.assets?.length || 0
      });
      
      // Load chart of accounts if not already loaded
      let accounts = chartOfAccounts;
      if (!accounts || accounts.length === 0) {
        console.log('Loading chart of accounts from database...');
        accounts = await accountingService.getChartOfAccounts(schoolID);
        if (accounts && accounts.length > 0) {
          // Dispatch chart of accounts with validation
          if (setChartOfAccounts && typeof setChartOfAccounts === 'function') {
            try {
              const accountsAction = setChartOfAccounts(accounts);
              if (accountsAction && typeof accountsAction === 'object' && accountsAction.type) {
                dispatch(accountsAction);
              }
            } catch (err) {
              console.error('Error dispatching chart of accounts:', err);
            }
          }
          console.log('Loaded', accounts.length, 'accounts from database');
        } else {
          console.warn('No chart of accounts found. Balance sheet may be incomplete.');
        }
      } else {
        console.log('Using', accounts.length, 'accounts from Redux');
      }
      
      // Generate balance sheet from real data with chart of accounts and date range
      // Add timeout to prevent infinite loading (30 seconds)
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Balance sheet generation timed out after 30 seconds')), 30000);
      });
      
      console.log('Starting balance sheet generation...');
      const dataPromise = realDataAccountingService.generateRealBalanceSheet(
        schoolID,
        startDate,
        endDate,
        reduxData,
        accounts || []
      );
      
      let data;
      try {
        data = await Promise.race([dataPromise, timeoutPromise]);
      } catch (timeoutError) {
        console.error('Balance sheet generation timed out or failed:', timeoutError);
        throw new Error(timeoutError.message || 'Balance sheet generation timed out');
      }
      
      console.log('Balance sheet data received:', data);
      
      // Ensure data structure is valid
      if (!data) {
        throw new Error('Balance sheet data is null or undefined');
      }
      
      // Validate data structure
      if (!data.assets || !data.liabilities || !data.equity) {
        console.warn('Balance sheet data structure incomplete:', data);
        // Create minimal structure if missing
        if (!data.assets) data.assets = { currentAssets: {}, fixedAssets: {}, totalAssets: 0 };
        if (!data.liabilities) data.liabilities = { currentLiabilities: {}, longTermLiabilities: {}, totalLiabilities: 0 };
        if (!data.equity) data.equity = { capital: 0, retainedEarnings: 0, totalEquity: 0 };
      }
      
      // Ensure all required properties exist
      if (!data.assets.totalAssets && data.assets.totalAssets !== 0) {
        data.assets.totalAssets = (data.assets.currentAssets?.total || 0) + (data.assets.fixedAssets?.total || 0);
      }
      if (!data.liabilities.totalLiabilities && data.liabilities.totalLiabilities !== 0) {
        data.liabilities.totalLiabilities = (data.liabilities.currentLiabilities?.total || 0) + (data.liabilities.longTermLiabilities?.total || 0);
      }
      if (!data.equity.totalEquity && data.equity.totalEquity !== 0) {
        data.equity.totalEquity = (data.equity.capital || 0) + (data.equity.retainedEarnings || 0);
      }
      
      // Dispatch balance sheet with validation
      if (setBalanceSheet && typeof setBalanceSheet === 'function') {
        try {
          const balanceSheetAction = setBalanceSheet(data);
          if (balanceSheetAction && typeof balanceSheetAction === 'object' && balanceSheetAction.type) {
            dispatch(balanceSheetAction);
          } else {
            console.error('setBalanceSheet returned invalid action:', balanceSheetAction);
          }
        } catch (err) {
          console.error('Error dispatching balance sheet:', err);
        }
      }
      console.log('Balance sheet dispatched to Redux');
    } catch (err) {
      console.error('Balance sheet error:', err);
      const errorMessage = 'Failed to load balance sheet: ' + (err.message || 'Unknown error');
      setError(errorMessage);
      
      // Dispatch error with validation
      if (setReduxError && typeof setReduxError === 'function') {
        try {
          const errorAction = setReduxError(errorMessage);
          if (errorAction && typeof errorAction === 'object' && errorAction.type) {
            dispatch(errorAction);
          }
        } catch (dispatchErr) {
          console.error('Error dispatching error:', dispatchErr);
        }
      }
      
      // Set empty balance sheet to prevent showing "No data" message
      if (setBalanceSheet && typeof setBalanceSheet === 'function') {
        try {
          const emptyBalanceSheetAction = setBalanceSheet({
            assets: { currentAssets: {}, fixedAssets: {}, totalAssets: 0 },
            liabilities: { currentLiabilities: {}, longTermLiabilities: {}, totalLiabilities: 0 },
            equity: { capital: 0, retainedEarnings: 0, totalEquity: 0 },
            error: errorMessage
          });
          if (emptyBalanceSheetAction && typeof emptyBalanceSheetAction === 'object' && emptyBalanceSheetAction.type) {
            dispatch(emptyBalanceSheetAction);
          }
        } catch (dispatchErr) {
          console.error('Error dispatching empty balance sheet:', dispatchErr);
        }
      }
    } finally {
      // Always set loading to false, even if there was an error or timeout
      console.log('Setting loading to false in finally block');
      setIsLoadingBalanceSheet(false);
      setLocalLoading(false);
      // Ensure Redux loading is also cleared
      // Check if setLoading is available before calling
      if (setLoading && typeof setLoading === 'function') {
        try {
          const loadingAction = setLoading({ balanceSheet: false });
          // Redux Toolkit actions are always objects, dispatch if valid
          if (loadingAction && typeof loadingAction === 'object') {
            dispatch(loadingAction);
          } else if (loadingAction === undefined) {
            // This shouldn't happen with Redux Toolkit, but log it
            console.warn('setLoading returned undefined - this may indicate a build/import issue');
          } else {
            console.warn('setLoading returned unexpected value:', typeof loadingAction, loadingAction);
          }
        } catch (err) {
          console.error('Error clearing Redux loading state:', err);
        }
      } else {
        console.warn('setLoading is not available, cannot clear Redux loading state');
      }
      console.log('Loading state updated to false');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolID, startDate, endDate, reduxData, chartOfAccounts, dispatch]); // isLoadingBalanceSheet is only a guard, not a dependency

  useEffect(() => {
    // Only run if schoolID is available and all required actions are defined
    if (schoolID && setLoading && setBalanceSheet && setReduxError && setChartOfAccounts) {
      loadBalanceSheet();
    } else if (!schoolID) {
      // Clear any existing balance sheet if school is not selected
      setError(null);
      setLocalLoading(false);
      setIsLoadingBalanceSheet(false);
      // Clear Redux loading state only if setLoading is available
      if (setLoading && typeof setLoading === 'function') {
        try {
          const loadingAction = setLoading({ balanceSheet: false });
          if (loadingAction && typeof loadingAction === 'object') {
            dispatch(loadingAction);
          } else if (loadingAction === undefined) {
            console.warn('setLoading returned undefined in useEffect');
          }
        } catch (err) {
          console.error('Error clearing Redux loading in useEffect:', err);
        }
      }
    } else {
      // If actions are not available, set loading to false
      setLocalLoading(false);
      setIsLoadingBalanceSheet(false);
      // Clear Redux loading state only if setLoading is available
      if (setLoading && typeof setLoading === 'function') {
        try {
          const loadingAction = setLoading({ balanceSheet: false });
          if (loadingAction && typeof loadingAction === 'object') {
            dispatch(loadingAction);
          } else if (loadingAction === undefined) {
            console.warn('setLoading returned undefined in useEffect');
          }
        } catch (err) {
          console.error('Error clearing Redux loading in useEffect:', err);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolID, startDate, endDate]); // Removed loadBalanceSheet and reduxData to prevent infinite loops

  const handleRefresh = () => {
    loadBalanceSheet();
  };

  const handlePrint = () => {
    if (!balanceSheet) {
      setError('No balance sheet data available to print');
      return;
    }

    // Create a print-friendly window
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setError('Please allow popups to print the balance sheet');
      return;
    }

    const schoolName = currentSchool?.schoolname || currentSchool?.schoolName || 'School';
    const period = balanceSheet.startDate && balanceSheet.endDate
      ? `For the period from ${new Date(balanceSheet.startDate).toLocaleDateString()} to ${new Date(balanceSheet.endDate).toLocaleDateString()}`
      : `As of ${new Date(balanceSheet.asOfDate || endDate).toLocaleDateString()}`;

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Balance Sheet - ${schoolName}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { text-align: center; margin-bottom: 5px; }
            h2 { text-align: center; color: #666; margin-top: 5px; margin-bottom: 20px; }
            .balance-info { text-align: center; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .total-row { font-weight: bold; background-color: #f5f5f5; }
            .section-header { font-weight: bold; background-color: #e0e0e0; }
            .right-align { text-align: right; }
            .balanced { color: green; font-weight: bold; }
            .unbalanced { color: red; font-weight: bold; }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <h1>${schoolName}</h1>
          <h2>Balance Sheet</h2>
          <div class="balance-info">${period}</div>
          <div class="balance-info">
            Generated: ${new Date().toLocaleString()}
          </div>
          
          <table>
            <tr>
              <th style="width: 50%;">ASSETS</th>
              <th style="width: 50%;">LIABILITIES & EQUITY</th>
            </tr>
            <tr>
              <td style="vertical-align: top;">
                <table>
                  <tr class="section-header"><td colspan="2">Current Assets</td></tr>
                  <tr><td>Cash and Cash Equivalents</td><td class="right-align">${formatCurrency(balanceSheet.assets?.currentAssets?.cash || 0)}</td></tr>
                  <tr><td>Accounts Receivable</td><td class="right-align">${formatCurrency(balanceSheet.assets?.currentAssets?.receivables || 0)}</td></tr>
                  <tr><td>Inventory</td><td class="right-align">${formatCurrency(balanceSheet.assets?.currentAssets?.inventory || 0)}</td></tr>
                  <tr><td>Prepaid Expenses</td><td class="right-align">${formatCurrency(balanceSheet.assets?.currentAssets?.prepaid || 0)}</td></tr>
                  <tr class="total-row"><td>Total Current Assets</td><td class="right-align">${formatCurrency(balanceSheet.assets?.currentAssets?.total || 0)}</td></tr>
                  
                  <tr class="section-header"><td colspan="2">Fixed Assets</td></tr>
                  <tr><td>Land and Buildings</td><td class="right-align">${formatCurrency(balanceSheet.assets?.fixedAssets?.landBuildings || 0)}</td></tr>
                  <tr><td>Equipment</td><td class="right-align">${formatCurrency(balanceSheet.assets?.fixedAssets?.equipment || 0)}</td></tr>
                  <tr><td>Furniture and Fixtures</td><td class="right-align">${formatCurrency(balanceSheet.assets?.fixedAssets?.furniture || 0)}</td></tr>
                  <tr><td>Less: Accumulated Depreciation</td><td class="right-align">(${formatCurrency(balanceSheet.assets?.fixedAssets?.depreciation || 0)})</td></tr>
                  <tr class="total-row"><td>Total Fixed Assets</td><td class="right-align">${formatCurrency(balanceSheet.assets?.fixedAssets?.total || 0)}</td></tr>
                  
                  <tr class="total-row section-header"><td>TOTAL ASSETS</td><td class="right-align">${formatCurrency(balanceSheet.assets?.totalAssets || 0)}</td></tr>
                </table>
              </td>
              <td style="vertical-align: top;">
                <table>
                  <tr class="section-header"><td colspan="2">Current Liabilities</td></tr>
                  <tr><td>Accounts Payable</td><td class="right-align">${formatCurrency(balanceSheet.liabilities?.currentLiabilities?.payables || 0)}</td></tr>
                  <tr><td>Accrued Expenses</td><td class="right-align">${formatCurrency(balanceSheet.liabilities?.currentLiabilities?.accrued || 0)}</td></tr>
                  <tr><td>Short-term Debt</td><td class="right-align">${formatCurrency(balanceSheet.liabilities?.currentLiabilities?.shortTermDebt || 0)}</td></tr>
                  <tr class="total-row"><td>Total Current Liabilities</td><td class="right-align">${formatCurrency(balanceSheet.liabilities?.currentLiabilities?.total || 0)}</td></tr>
                  
                  <tr class="section-header"><td colspan="2">Long-term Liabilities</td></tr>
                  <tr><td>Long-term Debt</td><td class="right-align">${formatCurrency(balanceSheet.liabilities?.longTermLiabilities?.longTermDebt || 0)}</td></tr>
                  <tr class="total-row"><td>Total Long-term Liabilities</td><td class="right-align">${formatCurrency(balanceSheet.liabilities?.longTermLiabilities?.total || 0)}</td></tr>
                  
                  <tr class="total-row"><td>TOTAL LIABILITIES</td><td class="right-align">${formatCurrency(balanceSheet.liabilities?.totalLiabilities || 0)}</td></tr>
                  
                  <tr class="section-header"><td colspan="2">Equity</td></tr>
                  <tr><td>Capital</td><td class="right-align">${formatCurrency(balanceSheet.equity?.capital || 0)}</td></tr>
                  <tr><td>Retained Earnings</td><td class="right-align">${formatCurrency(balanceSheet.equity?.retainedEarnings || 0)}</td></tr>
                  <tr class="total-row"><td>Total Equity</td><td class="right-align">${formatCurrency(balanceSheet.equity?.totalEquity || 0)}</td></tr>
                  
                  <tr class="total-row section-header"><td>TOTAL LIABILITIES & EQUITY</td><td class="right-align">${formatCurrency((balanceSheet.liabilities?.totalLiabilities || 0) + (balanceSheet.equity?.totalEquity || 0))}</td></tr>
                </table>
              </td>
            </tr>
          </table>
          
          <div class="balance-info ${balanceSheet.balanced ? 'balanced' : 'unbalanced'}">
            ${balanceSheet.balanced 
              ? '✓ Balance Sheet is Balanced' 
              : `⚠ Balance Sheet Difference: ${formatCurrency(Math.abs(balanceSheet.difference || 0))}`}
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    
    // Wait for content to load, then print
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const handleExport = async (format = 'pdf') => {
    if (!balanceSheet) {
      setError('No balance sheet data available to export');
      return;
    }

    try {
      const schoolName = currentSchool?.schoolname || currentSchool?.schoolName || 'School';
      const period = balanceSheet.startDate && balanceSheet.endDate
        ? `${new Date(balanceSheet.startDate).toLocaleDateString()} to ${new Date(balanceSheet.endDate).toLocaleDateString()}`
        : new Date(balanceSheet.asOfDate || endDate).toLocaleDateString();
      const fileName = `balance_sheet_${startDate}_to_${endDate}`;

      if (format === 'pdf') {
        // Generate PDF using jsPDF
        const doc = new jsPDF('landscape', 'mm', 'a4');
        
        // Title
        doc.setFontSize(18);
        doc.text(schoolName, 148, 15, { align: 'center' });
        doc.setFontSize(14);
        doc.text('Balance Sheet', 148, 22, { align: 'center' });
        doc.setFontSize(10);
        doc.text(`For the period from ${period}`, 148, 28, { align: 'center' });
        doc.text(`Generated: ${new Date().toLocaleString()}`, 148, 33, { align: 'center' });

        // Assets table
        const assetsData = [
          ['Current Assets', ''],
          ['Cash and Cash Equivalents', formatCurrency(balanceSheet.assets?.currentAssets?.cash || 0)],
          ['Accounts Receivable', formatCurrency(balanceSheet.assets?.currentAssets?.receivables || 0)],
          ['Inventory', formatCurrency(balanceSheet.assets?.currentAssets?.inventory || 0)],
          ['Prepaid Expenses', formatCurrency(balanceSheet.assets?.currentAssets?.prepaid || 0)],
          ['Total Current Assets', formatCurrency(balanceSheet.assets?.currentAssets?.total || 0)],
          ['', ''],
          ['Fixed Assets', ''],
          ['Land and Buildings', formatCurrency(balanceSheet.assets?.fixedAssets?.landBuildings || 0)],
          ['Equipment', formatCurrency(balanceSheet.assets?.fixedAssets?.equipment || 0)],
          ['Furniture and Fixtures', formatCurrency(balanceSheet.assets?.fixedAssets?.furniture || 0)],
          ['Less: Accumulated Depreciation', `(${formatCurrency(balanceSheet.assets?.fixedAssets?.depreciation || 0)})`],
          ['Total Fixed Assets', formatCurrency(balanceSheet.assets?.fixedAssets?.total || 0)],
          ['', ''],
          ['TOTAL ASSETS', formatCurrency(balanceSheet.assets?.totalAssets || 0)]
        ];

        doc.autoTable({
          startY: 40,
          head: [['ASSETS', 'Amount']],
          body: assetsData,
          theme: 'striped',
          headStyles: { fillColor: [66, 139, 202], textColor: 255, fontStyle: 'bold' },
          styles: { fontSize: 9 },
          columnStyles: {
            0: { cellWidth: 120 },
            1: { cellWidth: 60, halign: 'right' }
          },
          didParseCell: (data) => {
            if (data.row.index === 0 || data.row.index === 6 || data.row.index === 7 || data.row.index === 13 || data.row.index === 14) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [240, 240, 240];
            }
            if (data.row.index === 5 || data.row.index === 12 || data.row.index === 14) {
              data.cell.styles.fontStyle = 'bold';
            }
          }
        });

        // Liabilities & Equity table
        const liabilitiesData = [
          ['Current Liabilities', ''],
          ['Accounts Payable', formatCurrency(balanceSheet.liabilities?.currentLiabilities?.payables || 0)],
          ['Accrued Expenses', formatCurrency(balanceSheet.liabilities?.currentLiabilities?.accrued || 0)],
          ['Short-term Debt', formatCurrency(balanceSheet.liabilities?.currentLiabilities?.shortTermDebt || 0)],
          ['Total Current Liabilities', formatCurrency(balanceSheet.liabilities?.currentLiabilities?.total || 0)],
          ['', ''],
          ['Long-term Liabilities', ''],
          ['Long-term Debt', formatCurrency(balanceSheet.liabilities?.longTermLiabilities?.longTermDebt || 0)],
          ['Total Long-term Liabilities', formatCurrency(balanceSheet.liabilities?.longTermLiabilities?.total || 0)],
          ['', ''],
          ['TOTAL LIABILITIES', formatCurrency(balanceSheet.liabilities?.totalLiabilities || 0)],
          ['', ''],
          ['Equity', ''],
          ['Capital', formatCurrency(balanceSheet.equity?.capital || 0)],
          ['Retained Earnings', formatCurrency(balanceSheet.equity?.retainedEarnings || 0)],
          ['Total Equity', formatCurrency(balanceSheet.equity?.totalEquity || 0)],
          ['', ''],
          ['TOTAL LIABILITIES & EQUITY', formatCurrency((balanceSheet.liabilities?.totalLiabilities || 0) + (balanceSheet.equity?.totalEquity || 0))]
        ];

        const finalY = doc.lastAutoTable?.finalY || 40;
        doc.autoTable({
          startY: 40,
          startX: 150,
          head: [['LIABILITIES & EQUITY', 'Amount']],
          body: liabilitiesData,
          theme: 'striped',
          headStyles: { fillColor: [220, 53, 69], textColor: 255, fontStyle: 'bold' },
          styles: { fontSize: 9 },
          columnStyles: {
            0: { cellWidth: 120 },
            1: { cellWidth: 60, halign: 'right' }
          },
          didParseCell: (data) => {
            if (data.row.index === 0 || data.row.index === 5 || data.row.index === 6 || data.row.index === 9 || data.row.index === 10 || data.row.index === 12 || data.row.index === 17 || data.row.index === 18) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [240, 240, 240];
            }
            if (data.row.index === 4 || data.row.index === 8 || data.row.index === 10 || data.row.index === 16 || data.row.index === 18) {
              data.cell.styles.fontStyle = 'bold';
            }
          }
        });

        // Balance status
        const statusY = Math.max(doc.lastAutoTable?.finalY || 200, finalY) + 10;
        doc.setFontSize(12);
        if (balanceSheet.balanced) {
          doc.setTextColor(0, 128, 0);
        } else {
          doc.setTextColor(255, 0, 0);
        }
        doc.text(
          balanceSheet.balanced 
            ? '✓ Balance Sheet is Balanced' 
            : `⚠ Balance Sheet Difference: ${formatCurrency(Math.abs(balanceSheet.difference || 0))}`,
          148,
          statusY,
          { align: 'center' }
        );

        doc.save(`${fileName}.pdf`);
      } else if (format === 'excel') {
        // Generate Excel using xlsx
        const workbook = XLSX.utils.book_new();

        // Create Assets sheet
        const assetsSheetData = [
          ['ASSETS', ''],
          ['Current Assets', ''],
          ['Cash and Cash Equivalents', balanceSheet.assets?.currentAssets?.cash || 0],
          ['Accounts Receivable', balanceSheet.assets?.currentAssets?.receivables || 0],
          ['Inventory', balanceSheet.assets?.currentAssets?.inventory || 0],
          ['Prepaid Expenses', balanceSheet.assets?.currentAssets?.prepaid || 0],
          ['Total Current Assets', balanceSheet.assets?.currentAssets?.total || 0],
          ['', ''],
          ['Fixed Assets', ''],
          ['Land and Buildings', balanceSheet.assets?.fixedAssets?.landBuildings || 0],
          ['Equipment', balanceSheet.assets?.fixedAssets?.equipment || 0],
          ['Furniture and Fixtures', balanceSheet.assets?.fixedAssets?.furniture || 0],
          ['Less: Accumulated Depreciation', -(balanceSheet.assets?.fixedAssets?.depreciation || 0)],
          ['Total Fixed Assets', balanceSheet.assets?.fixedAssets?.total || 0],
          ['', ''],
          ['TOTAL ASSETS', balanceSheet.assets?.totalAssets || 0]
        ];

        const assetsWS = XLSX.utils.aoa_to_sheet(assetsSheetData);
        XLSX.utils.book_append_sheet(workbook, assetsWS, 'Assets');

        // Create Liabilities & Equity sheet
        const liabilitiesSheetData = [
          ['LIABILITIES & EQUITY', ''],
          ['Current Liabilities', ''],
          ['Accounts Payable', balanceSheet.liabilities?.currentLiabilities?.payables || 0],
          ['Accrued Expenses', balanceSheet.liabilities?.currentLiabilities?.accrued || 0],
          ['Short-term Debt', balanceSheet.liabilities?.currentLiabilities?.shortTermDebt || 0],
          ['Total Current Liabilities', balanceSheet.liabilities?.currentLiabilities?.total || 0],
          ['', ''],
          ['Long-term Liabilities', ''],
          ['Long-term Debt', balanceSheet.liabilities?.longTermLiabilities?.longTermDebt || 0],
          ['Total Long-term Liabilities', balanceSheet.liabilities?.longTermLiabilities?.total || 0],
          ['', ''],
          ['TOTAL LIABILITIES', balanceSheet.liabilities?.totalLiabilities || 0],
          ['', ''],
          ['Equity', ''],
          ['Capital', balanceSheet.equity?.capital || 0],
          ['Retained Earnings', balanceSheet.equity?.retainedEarnings || 0],
          ['Total Equity', balanceSheet.equity?.totalEquity || 0],
          ['', ''],
          ['TOTAL LIABILITIES & EQUITY', (balanceSheet.liabilities?.totalLiabilities || 0) + (balanceSheet.equity?.totalEquity || 0)],
          ['', ''],
          ['Balance Status', balanceSheet.balanced ? 'Balanced' : `Difference: ${balanceSheet.difference || 0}`]
        ];

        const liabilitiesWS = XLSX.utils.aoa_to_sheet(liabilitiesSheetData);
        XLSX.utils.book_append_sheet(workbook, liabilitiesWS, 'Liabilities & Equity');

        // Create Summary sheet
        const summaryData = [
          ['Balance Sheet Summary', ''],
          ['School Name', schoolName],
          ['Period', period],
          ['Generated', new Date().toLocaleString()],
          ['', ''],
          ['Total Assets', balanceSheet.assets?.totalAssets || 0],
          ['Total Liabilities', balanceSheet.liabilities?.totalLiabilities || 0],
          ['Total Equity', balanceSheet.equity?.totalEquity || 0],
          ['Total Liabilities & Equity', (balanceSheet.liabilities?.totalLiabilities || 0) + (balanceSheet.equity?.totalEquity || 0)],
          ['Difference', balanceSheet.difference || 0],
          ['Status', balanceSheet.balanced ? 'Balanced' : 'Unbalanced']
        ];

        const summaryWS = XLSX.utils.aoa_to_sheet(summaryData);
        XLSX.utils.book_append_sheet(workbook, summaryWS, 'Summary');

        XLSX.writeFile(workbook, `${fileName}.xlsx`);
      }
    } catch (err) {
      console.error('Export error:', err);
      setError(`Error exporting ${format.toUpperCase()}: ${err.message || 'Unknown error'}`);
    }
  };

  const formatCurrency = (amount) => accountingService.formatCurrency(amount);

  // Debug logging
  console.log('BalanceSheet render - balanceSheet:', balanceSheet);
  console.log('BalanceSheet render - loading:', loading);
  console.log('BalanceSheet render - reduxLoading:', reduxLoading);
  console.log('BalanceSheet render - error:', error);

  // Check both local and Redux loading states - handle Redux loading properly
  const isLoading = loading || isLoadingBalanceSheet || (reduxLoading?.balanceSheet === true);
  
  if (isLoading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading balance sheet...</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This may take a few moments...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '100%' }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={handleRefresh}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Balance Sheet
        </Typography>
        <Box>
          <Tooltip title="Refresh Data">
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  startIcon={<PrintIcon />}
                  onClick={handlePrint}
                  disabled={!balanceSheet}
                >
                  Print
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExport('pdf')}
                  disabled={!balanceSheet}
                >
                  PDF
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExport('excel')}
                  disabled={!balanceSheet}
                >
                  Excel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Balance Sheet */}
      {balanceSheet && (
        <Paper>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" align="center">
              {currentSchool?.schoolname || currentSchool?.schoolName || 'School'} Balance Sheet
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              For the period from {accountingService.formatDate(startDate)} to {accountingService.formatDate(endDate)}
            </Typography>
            {balanceSheet?.balanced !== undefined && (
              <Typography 
                variant="body2" 
                align="center" 
                sx={{ 
                  mt: 1,
                  color: balanceSheet.balanced ? 'success.main' : 'error.main',
                  fontWeight: 'bold'
                }}
              >
                {balanceSheet.balanced 
                  ? '✓ Balance Sheet is Balanced' 
                  : `⚠ Balance Sheet Difference: ${accountingService.formatCurrency(Math.abs(balanceSheet.difference || 0))}`}
              </Typography>
            )}
          </Box>
          
          <Grid container spacing={0}>
            {/* Assets Column */}
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
                  ASSETS
                </Typography>
                
                {/* Current Assets */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Current Assets
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Cash and Cash Equivalents</TableCell>
                          <TableCell align="right">
                            {formatCurrency(balanceSheet.assets?.currentAssets?.cash || 0)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Accounts Receivable</TableCell>
                          <TableCell align="right">
                            {formatCurrency(balanceSheet.assets?.currentAssets?.receivables || 0)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Inventory</TableCell>
                          <TableCell align="right">
                            {formatCurrency(balanceSheet.assets?.currentAssets?.inventory || 0)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Prepaid Expenses</TableCell>
                          <TableCell align="right">
                            {formatCurrency(balanceSheet.assets?.currentAssets?.prepaid || 0)}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ borderTop: 2, borderColor: 'primary.main' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Total Current Assets</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(balanceSheet.assets?.currentAssets?.total || 0)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Fixed Assets */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Fixed Assets
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Land and Buildings</TableCell>
                          <TableCell align="right">
                            {formatCurrency(balanceSheet.assets?.fixedAssets?.landBuildings || 0)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Equipment</TableCell>
                          <TableCell align="right">
                            {formatCurrency(balanceSheet.assets?.fixedAssets?.equipment || 0)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Furniture and Fixtures</TableCell>
                          <TableCell align="right">
                            {formatCurrency(balanceSheet.assets?.fixedAssets?.furniture || 0)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Less: Accumulated Depreciation</TableCell>
                          <TableCell align="right" sx={{ color: 'error.main' }}>
                            ({formatCurrency(balanceSheet.assets?.fixedAssets?.depreciation || 0)})
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ borderTop: 2, borderColor: 'primary.main' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Total Fixed Assets</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(balanceSheet.assets?.fixedAssets?.total || 0)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Total Assets */}
                <Box>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow sx={{ borderTop: 3, borderColor: 'primary.main', backgroundColor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>TOTAL ASSETS</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                            {formatCurrency(balanceSheet.assets?.totalAssets || 0)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            </Grid>

            {/* Liabilities and Equity Column */}
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#d32f2f' }}>
                  LIABILITIES & EQUITY
                </Typography>
                
                {/* Current Liabilities */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Current Liabilities
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Accounts Payable</TableCell>
                          <TableCell align="right">
                            {formatCurrency(balanceSheet.liabilities?.currentLiabilities?.payables || 0)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Accrued Expenses</TableCell>
                          <TableCell align="right">
                            {formatCurrency(balanceSheet.liabilities?.currentLiabilities?.accrued || 0)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Short-term Debt</TableCell>
                          <TableCell align="right">
                            {formatCurrency(balanceSheet.liabilities?.currentLiabilities?.shortTermDebt || 0)}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ borderTop: 2, borderColor: 'primary.main' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Total Current Liabilities</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(balanceSheet.liabilities?.currentLiabilities?.total || 0)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Long-term Liabilities */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Long-term Liabilities
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Long-term Debt</TableCell>
                          <TableCell align="right">
                            {formatCurrency(balanceSheet.liabilities?.longTermLiabilities?.longTermDebt || 0)}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ borderTop: 2, borderColor: 'primary.main' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Total Long-term Liabilities</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(balanceSheet.liabilities?.longTermLiabilities?.total || 0)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Total Liabilities */}
                <Box sx={{ mb: 2 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow sx={{ borderTop: 2, borderColor: 'primary.main', backgroundColor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>TOTAL LIABILITIES</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(balanceSheet.liabilities?.totalLiabilities || 0)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Equity */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Equity
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Capital</TableCell>
                          <TableCell align="right">
                            {formatCurrency(balanceSheet.equity?.capital || 0)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Retained Earnings</TableCell>
                          <TableCell align="right">
                            {formatCurrency(balanceSheet.equity?.retainedEarnings || 0)}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ borderTop: 2, borderColor: 'primary.main' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Total Equity</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(balanceSheet.equity?.totalEquity || 0)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Total Liabilities and Equity */}
                <Box>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow sx={{ borderTop: 3, borderColor: 'primary.main', backgroundColor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>TOTAL LIABILITIES & EQUITY</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                            {formatCurrency((balanceSheet.liabilities?.totalLiabilities || 0) + (balanceSheet.equity?.totalEquity || 0))}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {(!balanceSheet || balanceSheet.error) && !loading && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <AccountBalanceIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              {balanceSheet?.error ? 'Error loading balance sheet' : 'No balance sheet data available'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {balanceSheet?.error || 'Select a date range and click refresh to generate balance sheet'}
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mt: 2, maxWidth: 600, mx: 'auto' }}>
                {error}
              </Alert>
            )}
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              sx={{ mt: 2 }}
            >
              Refresh
            </Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default BalanceSheet;
